import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { PasswordField } from '@/components/ui/PasswordField';

import { credentialsSchema, type CredentialsValues } from './schemas';

interface CredentialsFormProps {
  /**
   * Fills the address in, for somebody arriving from the invitation page who
   * has just proved they own it.
   */
  defaultEmail?: string;
  onSubmit: (values: CredentialsValues) => void;
  pending: boolean;
}

export function CredentialsForm({
  onSubmit,
  pending,
  defaultEmail,
}: CredentialsFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: defaultEmail ?? '', password: '' },
  });

  return (
    <Box
      component="form"
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      noValidate
      data-testid="sign-in-form"
    >
      <Field label="Email address" error={errors.email?.message} testId="sign-in-email">
        <TextField type="email" autoComplete="username" {...register('email')} />
      </Field>

      <Field label="Password" error={errors.password?.message} testId="sign-in-password">
        <PasswordField autoComplete="current-password" {...register('password')} />
      </Field>

      <Button
        type="submit"
        loading={pending}
        data-testid="sign-in-submit-button"
        sx={{ mt: 1 }}
      >
        Sign in
      </Button>
    </Box>
  );
}
